/**
 * Spend Rules List Block - Frontend View
 *
 * Renders the spend rules list on the frontend with dynamic data.
 * Only hydrates for enrolled users who can actually redeem rewards.
 */
import React, { type FC, useState, useEffect } from 'react';
import { createRoot } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { dispatch } from '@wordpress/data';
import { useEnrollment } from '../_shared/hooks/use-enrollment';
import { ENROLLMENT_STORE_NAME, type Contact, type Tier, type ClaimedReward } from '../_shared/store/enrollment-store';
import Button from '../_shared/button';
import { getTranslatedText } from '../_shared/utils/translated-text';
import { replaceStrings } from '../_shared/utils/replacers';
import { joinProgram } from '~/shared/queries';
import {
	Item,
	ItemContent,
	ItemMedia,
	ItemLabel,
	ItemMeta,
	ItemBadge,
	ItemActions,
	ItemDescription,
	ItemSuccess,
	CouponCode,
} from '../_shared/components';
import { SpendRulesGridSkeleton } from '../_shared/skeleton/patterns';
import { __ } from '@wordpress/i18n';

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	showRulesToNotEnrolled: boolean;
	cardLabelLevel: number;
	buttonTextSignIn: string;
	buttonTextJoin: string;
	buttonTextRedeem: string;
	messageInsufficientCredits: string;
	messageRedeemSuccess: string;
	messageGuest: string;
	messageNotEnrolled: string;
	emptyStateMessage: string;
	emptyStateButtonTextGuest: string;
	emptyStateButtonTextNotEnrolled: string;
	progressMeterText: string;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
	showCostBadge: boolean;
	buttonTextColor?: string; // Preset slug for button
	buttonBackgroundColor?: string; // Preset slug for button
	buttonStyle?: {
		color?: {
			text?: string;
			background?: string;
		};
	};
	costBadgeTextColor?: string; // Preset slug for badge
	costBadgeBackgroundColor?: string; // Preset slug for badge
	buttonTextLocationOnly: string;
	costBadgeStyle?: {
		color?: {
			text?: string;
			background?: string;
		};
	};
}

interface SpendRule {
	id: number;
	label: { value: string | Record<string, string> };
	creditCost: { value: number };
	svg: string;
	image?: { value: string };
	description?: { value: string | Record<string, string> };
	discount?: { value: number }; // For discount-based rewards
	offlineOnly?: { value: string };
}

interface SpendRulesListViewProps {
	attributes: Attributes;
	initialContent?: string;
}

// ============================================================================
// State Components
// ============================================================================

const GuestState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	// Use WooCommerce my-account page with redirect back to current page
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	return (
		<div className="leat-spend-rules-list__empty-state">
			<p>{attributes.messageGuest}</p>
			<Button
				href={redirectUrl}
				variant="contained"
			>
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const NotEnrolledEmptyState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			const userId = window.leat?.userId ?? null;
			if (!userId) {
				throw new Error('User ID not found');
			}

			await joinProgram(userId);
			window.location.reload();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to join program';
			setJoinError(message);
			setIsJoining(false);
		}
	};

	return (
		<div className="leat-spend-rules-list__empty-state">
			<p>{attributes.messageNotEnrolled}</p>
			<Button
				onClick={handleJoinProgram}
				disabled={isJoining}
				variant="contained"
			>
				{isJoining ? 'Joining...' : attributes.emptyStateButtonTextNotEnrolled}
			</Button>
			{joinError && <p className="leat-spend-rules-list__error">{joinError}</p>}
		</div>
	);
};

const LoggedInNotEnrolledState: FC<{
	attributes: Attributes;
	spendRules: SpendRule[];
}> = ({ attributes, spendRules }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			// Get current user ID from window.leat
			const userId = window.leat?.userId ?? null;

			if (!userId) {
				throw new Error('User ID not found');
			}

			await joinProgram(userId);

			// Reload the page to refresh enrollment status
			window.location.reload();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to join program';
			setJoinError(message);
			setIsJoining(false);
		}
	};

	// Get button inline styles
	const buttonInlineStyles: React.CSSProperties = {};

	buttonInlineStyles.borderColor = 'transparent';
	if (attributes.buttonStyle?.color?.text) {
		buttonInlineStyles.color = attributes.buttonStyle.color.text;
	} else if (attributes.buttonTextColor) {
		buttonInlineStyles.color = `var(--wp--preset--color--${attributes.buttonTextColor})`;
	}
	if (attributes.buttonStyle?.color?.background) {
		buttonInlineStyles.backgroundColor = attributes.buttonStyle.color.background;
	} else if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = `var(--wp--preset--color--${attributes.buttonBackgroundColor})`;
	}

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<>
			<div className="leat-spend-rules-list__grid" style={gridStyle}>
				{spendRules.map((rule) => (
					<NotEnrolledSpendRuleCard
						key={rule.id}
						rule={rule}
						labelLevel={attributes.cardLabelLevel}
						buttonTextJoin={attributes.buttonTextJoin}
						buttonInlineStyles={buttonInlineStyles}
						showCostBadge={attributes.showCostBadge}
						imageHeight={attributes.imageHeight}
						imageFit={attributes.imageFit}
						onJoinProgram={handleJoinProgram}
						isJoiningProgram={isJoining}
						joinProgramError={joinError}
					/>
				))}
			</div>
		</>
	);
};

// ============================================================================
// Component
// ============================================================================

const SpendRulesListView: FC<SpendRulesListViewProps> = ({ attributes, initialContent }) => {
	const { status, credits, creditsCurrency, isLoading: enrollmentLoading } = useEnrollment();
	const [spendRules, setSpendRules] = useState<SpendRule[]>([]);
	const [isLoadingRules, setIsLoadingRules] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hasReceivedData, setHasReceivedData] = useState(false);

	// Function to fetch spend rules
	const fetchSpendRules = async () => {
		// Don't fetch if still determining enrollment status
		if (enrollmentLoading) {
			return;
		}

		// Don't fetch for guests - they only see a sign-in button
		if (status === 'guest') {
			setIsLoadingRules(false);
			setHasReceivedData(true);
			return;
		}

		// Don't fetch for non-enrolled if not showing rules to them
		if (status === 'loggedInNotEnrolled' && !attributes.showRulesToNotEnrolled) {
			setIsLoadingRules(false);
			setHasReceivedData(true);
			return;
		}

		try {
			// Fetch spend rules for enrolled users and logged-in-not-enrolled (if showing rules)
			const rules = await apiFetch<SpendRule[]>({ path: '/leat/v1/spend-rules' });

			// Order by credit cost (highest first)
			const orderedRules = rules.sort((a, b) =>
				(b.creditCost.value ?? 0) - (a.creditCost.value ?? 0)
			);
			setSpendRules(orderedRules);
			setIsLoadingRules(false);
			setHasReceivedData(true);
		} catch (err) {
			// Check if this is a "user not enrolled" error (404 or specific error codes)
			// The error can be a Response object (with .status) or an error object (with .code/.data)
			const isNotEnrolledError =
				err.status === 404 || // Response object
				err.code === 'spend-rules-claim' ||
				err.code === 'no_contact_uuid' ||
				err.data?.status === 404 ||
				(err.message && err.message.toLowerCase().includes('not found')) ||
				(err.message && err.message.toLowerCase().includes('user not found'));

			if (isNotEnrolledError) {
				console.log('[Spend Rules] User not enrolled (404) - showing empty state');
				setSpendRules([]);
				setIsLoadingRules(false);
				setHasReceivedData(true);
			} else {
				// Real error - show error message
				console.error('[Spend Rules] API Error:', err);
				setError(err.message || 'Failed to load rewards');
				setIsLoadingRules(false);
			}
		}
	};

	// Fetch spend rules when needed
	useEffect(() => {
		fetchSpendRules();
	}, [status, enrollmentLoading, attributes.showRulesToNotEnrolled]);

	// Show initial server-rendered content while loading
	if (!hasReceivedData && initialContent) {
		return <div dangerouslySetInnerHTML={{ __html: initialContent }} />;
	}

	// Show loading state
	if (isLoadingRules || enrollmentLoading) {
		return (
			<SpendRulesGridSkeleton
				columns={attributes.columns}
				ariaMessage={__('Loading rewards...', 'leat-crm')}
			/>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="leat-spend-rules-list__error">
				<p>{error}</p>
			</div>
		);
	}

	// Show empty state ONLY for enrolled users with no rewards
	// Guest and non-enrolled states are handled below
	if (spendRules.length === 0 && hasReceivedData && status === 'enrolled') {
		return (
			<div className="leat-spend-rules-list__empty">
				<p>{attributes.emptyStateMessage}</p>
			</div>
		);
	}

	// ============================================================================
	// Handle Enrollment States
	// ============================================================================

	// Guest State: Show sign-in prompt
	if (status === 'guest') {
		return <GuestState attributes={attributes} />;
	}

	// Logged In Not Enrolled State: Show join program prompt
	if (status === 'loggedInNotEnrolled') {
		// If we want to show rules to not enrolled AND we have rules, show the grid
		if (attributes.showRulesToNotEnrolled && spendRules.length > 0) {
			return (
				<LoggedInNotEnrolledState
					attributes={attributes}
					spendRules={spendRules}
				/>
			);
		}
		// Otherwise show the empty state with join button
		return <NotEnrolledEmptyState attributes={attributes} />;
	}

	// ============================================================================
	// Enrolled State: Show Rewards List
	// ============================================================================

	// Get button inline styles from Gutenberg
	const buttonInlineStyles: React.CSSProperties = {};

	// Handle text color - check both custom and preset
	buttonInlineStyles.borderColor = 'transparent';

	if (attributes.buttonStyle?.color?.text) {
		buttonInlineStyles.color = attributes.buttonStyle.color.text;
	} else if (attributes.buttonTextColor) {
		buttonInlineStyles.color = `var(--wp--preset--color--${attributes.buttonTextColor})`;
	}

	// Handle background color - check both custom and preset
	if (attributes.buttonStyle?.color?.background) {
		buttonInlineStyles.backgroundColor = attributes.buttonStyle.color.background;
	} else if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = `var(--wp--preset--color--${attributes.buttonBackgroundColor})`;
	}

	// Get cost badge inline styles from Gutenberg
	const costBadgeInlineStyles: React.CSSProperties = {};

	// Handle badge text color - check both custom and preset
	if (attributes.costBadgeStyle?.color?.text) {
		costBadgeInlineStyles.color = attributes.costBadgeStyle.color.text;
	} else if (attributes.costBadgeTextColor) {
		costBadgeInlineStyles.color = `var(--wp--preset--color--${attributes.costBadgeTextColor})`;
	}

	// Handle badge background color - check both custom and preset
	if (attributes.costBadgeStyle?.color?.background) {
		costBadgeInlineStyles.backgroundColor = attributes.costBadgeStyle.color.background;
	} else if (attributes.costBadgeBackgroundColor) {
		costBadgeInlineStyles.backgroundColor = `var(--wp--preset--color--${attributes.costBadgeBackgroundColor})`;
	}

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<div className="leat-spend-rules-list__grid" style={gridStyle}>
			{spendRules.map((rule) => (
				<SpendRuleCard
					key={rule.id}
					rule={rule}
					userCredits={credits ?? 0}
					creditsCurrency={creditsCurrency ?? 'points'}
					labelLevel={attributes.cardLabelLevel}
					buttonTextRedeem={attributes.buttonTextRedeem}
					buttonInlineStyles={buttonInlineStyles}
					showCostBadge={attributes.showCostBadge}
					costBadgeInlineStyles={costBadgeInlineStyles}
					messageInsufficientCredits={attributes.messageInsufficientCredits}
					messageRedeemSuccess={attributes.messageRedeemSuccess}
					progressMeterText={attributes.progressMeterText}
					imageHeight={attributes.imageHeight}
					imageFit={attributes.imageFit}
					buttonTextLocationOnly={attributes.buttonTextLocationOnly}
					onRedeemSuccess={fetchSpendRules}
				/>
			))}
		</div>
	);
};

// ============================================================================
// Progress Meter Component
// ============================================================================

const ProgressMeter: FC<{
	currentBalance: number;
	requiredCredits: number;
	creditsCurrency: string;
	canAfford: boolean;
	progressMeterText: string;
}> = ({ currentBalance, requiredCredits, creditsCurrency, canAfford, progressMeterText }) => {
	// Calculate progress percentage
	const progressPercentage = Math.min((currentBalance / requiredCredits) * 100, 100);

	// Replace placeholders in the progress meter text
	const displayText = replaceStrings(progressMeterText, [
		{
			'{{current_balance}}': currentBalance.toString(),
			'{{credits_currency}}': creditsCurrency,
			'{{required_credits}}': requiredCredits.toString(),
		},
	]);

	return (
		<div className="leat-spend-rule-card__progress">
			<div className="leat-spend-rule-card__progress-bar">
				<div
					className="leat-spend-rule-card__progress-fill"
					style={{
						width: `${progressPercentage}%`,
						backgroundColor: canAfford
							? 'var(--wp--preset--color--vivid-green-cyan, #00d084)'
							: 'var(--wp--preset--color--vivid-cyan-blue, #0693e3)',
					}}
				/>
			</div>
			<div className="leat-spend-rule-card__progress-text">
				{displayText}
			</div>
		</div>
	);
};

// ============================================================================
// Card Component
// ============================================================================

const SpendRuleCard: FC<{
	rule: SpendRule;
	userCredits: number;
	creditsCurrency: string;
	labelLevel: number;
	buttonTextRedeem: string;
	buttonInlineStyles?: React.CSSProperties;
	showCostBadge: boolean;
	costBadgeInlineStyles?: React.CSSProperties;
	messageInsufficientCredits: string;
	messageRedeemSuccess: string;
	progressMeterText: string;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
	onRedeemSuccess?: () => void;
}> = ({ rule, userCredits, creditsCurrency, labelLevel, buttonTextRedeem, buttonInlineStyles, showCostBadge, costBadgeInlineStyles, messageInsufficientCredits, messageRedeemSuccess, progressMeterText, imageHeight, imageFit, buttonTextLocationOnly, onRedeemSuccess }) => {
	const [isRedeeming, setIsRedeeming] = useState(false);
	const [locallyRedeemed, setLocallyRedeemed] = useState<{ coupon: string } | null>(null);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);

	// Get translated label and description text
	const labelValue = rule.label.value;
	const label = typeof labelValue === 'string' ? labelValue : getTranslatedText(labelValue);
	const creditCost = rule.creditCost.value;

	// Get translated description if it exists
	const descriptionValue = rule.description?.value;
	const description = descriptionValue
		? (typeof descriptionValue === 'string' ? descriptionValue : getTranslatedText(descriptionValue))
		: null;

	// Prepare replacement variables
	const replacementVariables = {
		'{{credits}}': creditCost?.toString() || '0',
		'{{credits_currency}}': creditsCurrency,
		'{{discount}}': rule.discount?.value?.toString() || '0',
	};

	// Replace variables in label
	const displayLabel = replaceStrings(label, [replacementVariables]);

	// Replace variables in description
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;

	// Check if user can afford this reward
	const canAfford = userCredits >= creditCost;

	// Create dynamic heading tag
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	const handleRedeem = async (e?: React.MouseEvent) => {
		// Prevent any default behavior
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		if (!canAfford || isRedeeming) return;

		setIsRedeeming(true);
		try {
			const response = await apiFetch<{ success: boolean; coupon: string; reward_reception: any }>({
				path: `/leat/v1/spend-rules/${rule.id}/redeem`,
				method: 'POST',
			});

			console.log('[Spend Rules] Redeem success:', response);

			// Set locally redeemed state with coupon code
			if (response.coupon) {
				setLocallyRedeemed({ coupon: response.coupon });
				setShowSuccessMessage(true);

				// Hide success message and reset state after 5 seconds
				setTimeout(() => {
					setShowSuccessMessage(false);
					setLocallyRedeemed(null);
				}, 5000);
			}

			// Refresh enrollment data to get updated balance
			// Fetch fresh contact data and update the store
			const contactResponse = await apiFetch<{
				contact: Contact;
				tier: Tier | null;
				claimedRewards: ClaimedReward[];
			}>({
				path: '/leat/v1/contact',
			});

			console.log('[Spend Rules] Contact refetch response:', contactResponse);
			console.log('[Spend Rules] Contact balance:', contactResponse.contact?.balance);

			// Update the enrollment store with fresh data
			const storeDispatch = dispatch(ENROLLMENT_STORE_NAME) as any;
			storeDispatch.setContact(contactResponse.contact);
			storeDispatch.setTier(contactResponse.tier);
			storeDispatch.setClaimedRewards(contactResponse.claimedRewards);

			console.log('[Spend Rules] Enrollment data refreshed');

			// Refetch spend rules to update the list
			if (onRedeemSuccess) {
				await onRedeemSuccess();
				console.log('[Spend Rules] Spend rules list refreshed');
			}
		} catch (err) {
			console.error('[Spend Rules] Failed to redeem reward:', err);

			// Extract error message
			let errorMessage = __('Failed to redeem reward. Please try again.', 'leat-crm');
			if (err && typeof err === 'object') {
				if ('message' in err && typeof err.message === 'string') {
					errorMessage = err.message;
				} else if ('code' in err && typeof err.code === 'string') {
					errorMessage = err.code;
				}
			}

			alert(errorMessage);
		} finally {
			setIsRedeeming(false);
		}
	};

	return (
		<Item
			variant={!canAfford ? 'disabled' : 'default'}
			style={{
				'--leat-image-height': `${imageHeight}px`,
				'--leat-image-fit': imageFit,
			} as React.CSSProperties}
		>
			{/* Badge in top right corner */}
			{showCostBadge && creditCost !== null && (
				<ItemBadge style={costBadgeInlineStyles}>
					{creditCost} {creditsCurrency}
				</ItemBadge>
			)}

			<ItemContent>
				{rule.image?.value && (
					<ItemMedia>
						<img src={rule.image.value} alt={__('Reward image', 'leat-crm')} />
					</ItemMedia>
				)}

				<ItemLabel
					as={LabelTag}
					className={!displayDescription ? 'leat-spend-rule-card__label--no-description' : ''}
				>
					{displayLabel}
				</ItemLabel>

				{/* Add description if it exists */}
				{displayDescription && (
					<ItemDescription>{displayDescription}</ItemDescription>
				)}

				{/* Progress Meter - Show progress toward unlocking this reward */}
				{creditCost !== null && creditCost > 0 && (
					<ProgressMeter
						currentBalance={userCredits}
						requiredCredits={creditCost}
						creditsCurrency={creditsCurrency}
						canAfford={canAfford}
						progressMeterText={progressMeterText}
					/>
				)}

				{/* Success message with coupon code */}
				{showSuccessMessage && locallyRedeemed && (
					<>
						<ItemSuccess>
							{messageRedeemSuccess && !messageRedeemSuccess.includes('{{coupon}}')
								? messageRedeemSuccess
								: __('Reward redeemed successfully!', 'leat-crm')}
						</ItemSuccess>
						<ItemMeta variant="coupon-code">
							<CouponCode code={locallyRedeemed.coupon} buttonStyles={buttonInlineStyles} />
						</ItemMeta>
					</>
				)}
			</ItemContent>

			<ItemActions>
				{rule.offlineOnly?.value === 'on' ? (
					<Button
						renderAs="span"
						variant="contained"
						className="leat-button--small leat-button--location"
					>
						{buttonTextLocationOnly}
					</Button>
				) : (
					<>
						<Button
							type="button"
							variant="contained"
							className="leat-button--small"
							disabled={!canAfford || isRedeeming || showSuccessMessage}
							onClick={handleRedeem}
							title={!canAfford ? messageInsufficientCredits : undefined}
							style={buttonInlineStyles}
						>
							{isRedeeming
								? __('Redeeming...', 'leat-crm')
								: showSuccessMessage
								? __('Redeemed', 'leat-crm')
								: buttonTextRedeem}
						</Button>
						{!canAfford && !showSuccessMessage && (
							<div className="leat-item__insufficient">
								{messageInsufficientCredits}
							</div>
						)}
					</>
				)}
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Not Enrolled Spend Rule Card
// ============================================================================

const NotEnrolledSpendRuleCard: FC<{
	rule: SpendRule;
	labelLevel: number;
	buttonTextJoin: string;
	buttonInlineStyles?: React.CSSProperties;
	showCostBadge: boolean;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
	onJoinProgram?: () => void;
	isJoiningProgram?: boolean;
	joinProgramError?: string | null;
}> = ({
	rule,
	labelLevel,
	buttonTextJoin,
	buttonInlineStyles,
	showCostBadge,
	imageHeight,
	imageFit,
	onJoinProgram,
	isJoiningProgram = false,
	joinProgramError,
}) => {
	// Get translated label
	const labelValue = rule.label.value;
	const label = typeof labelValue === 'string' ? labelValue : getTranslatedText(labelValue);
	const creditCost = rule.creditCost.value;

	// Create dynamic heading tag
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	return (
		<Item
			variant="default"
			style={{
				'--leat-image-height': `${imageHeight}px`,
				'--leat-image-fit': imageFit,
			} as React.CSSProperties}
		>
			{/* Cost Badge in top right corner */}
			{showCostBadge && creditCost !== null && (
				<ItemBadge>{creditCost}</ItemBadge>
			)}

			<ItemContent>
				<ItemMedia>
					{rule.image?.value ? (
						<img src={rule.image.value} alt={label} />
					) : (
						// biome-ignore lint/security/noDangerouslySetInnerHtml: This is controlled SVG content from backend
						<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
					)}
				</ItemMedia>
				<ItemLabel as={LabelTag}>{label}</ItemLabel>
			</ItemContent>

			<ItemActions>
				<Button
					onClick={onJoinProgram}
					disabled={isJoiningProgram}
					variant="contained"
					className="leat-button--small"
					style={buttonInlineStyles}
				>
					{isJoiningProgram ? 'Joining...' : buttonTextJoin}
				</Button>
				{joinProgramError && (
					<div className="leat-item__error">{joinProgramError}</div>
				)}
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize spend rules list blocks
 * Hydrates for all enrollment states: guest, loggedInNotEnrolled, and enrolled
 */
export function initSpendRulesListBlocks(): void {
	// Wait for WordPress data stores to be ready
	if (typeof window.wp === 'undefined' || typeof window.wp.data === 'undefined') {
		setTimeout(initSpendRulesListBlocks, 100);
		return;
	}

	// Find all spend rules list blocks (all enrollment states)
	const blocks = document.querySelectorAll<HTMLElement>(
		'[data-block-type="spend-rules-list"]'
	);

	if (blocks.length === 0) {
		return;
	}

	// Hydrate each block
	for (const block of blocks) {
		// Parse the button style attribute if it exists
		let buttonStyle: Attributes['buttonStyle'];
		if (block.dataset.buttonStyle) {
			try {
				buttonStyle = JSON.parse(block.dataset.buttonStyle);
			} catch (e) {
				console.error('Failed to parse button style attribute:', e);
			}
		}

		// Parse the cost badge style attribute if it exists
		let costBadgeStyle: Attributes['costBadgeStyle'];
		if (block.dataset.costBadgeStyle) {
			try {
				costBadgeStyle = JSON.parse(block.dataset.costBadgeStyle);
			} catch (e) {
				console.error('Failed to parse cost badge style attribute:', e);
			}
		}

		const attributes: Attributes = {
			columns: parseInt(block.dataset.columns || '3', 10),
			showRulesToNotEnrolled: block.dataset.showRulesToNotEnrolled === 'true',
			cardLabelLevel: parseInt(block.dataset.cardLabelLevel || '4', 10),
			buttonTextSignIn: block.dataset.buttonTextSignIn || 'Sign In',
			buttonTextJoin: block.dataset.buttonTextJoin || 'Join Program',
			buttonTextRedeem: block.dataset.buttonTextRedeem || 'Redeem',
			messageInsufficientCredits: block.dataset.messageInsufficientCredits || 'Insufficient credits',
			messageRedeemSuccess: block.dataset.messageRedeemSuccess || 'Coupon code: {{coupon}}',
			messageGuest: block.dataset.messageGuest || 'Please sign in to see available rewards',
			messageNotEnrolled: block.dataset.messageNotEnrolled || 'Join our program to unlock exclusive rewards',
			emptyStateMessage: block.dataset.emptyStateMessage || 'No rewards available at this time',
			emptyStateButtonTextGuest: block.dataset.emptyStateButtonTextGuest || 'Sign In',
			emptyStateButtonTextNotEnrolled: block.dataset.emptyStateButtonTextNotEnrolled || 'Join Program',
			progressMeterText: block.dataset.progressMeterText || 'You have {{current_balance}} {{credits_currency}} out of {{required_credits}}',
			imageHeight: parseInt(block.dataset.imageHeight || '200', 10),
			imageFit: (block.dataset.imageFit as 'contain' | 'cover' | 'fill') || 'cover',
			showCostBadge: block.dataset.showCostBadge === 'true',
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
			buttonStyle: buttonStyle || undefined,
			costBadgeTextColor: block.dataset.costBadgeTextColor || undefined,
			costBadgeBackgroundColor: block.dataset.costBadgeBackgroundColor || undefined,
			costBadgeStyle: costBadgeStyle || undefined,
			buttonTextLocationOnly: block.dataset.buttonTextLocationOnly || 'Claimable on location only',
		};

		// Preserve initial server-rendered content
		const initialContent = block.innerHTML;

		// Create React root and render
		const root = createRoot(block);
		root.render(
			<SpendRulesListView
				attributes={attributes}
				initialContent={initialContent}
			/>
		);
	}
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initSpendRulesListBlocks);
} else {
	initSpendRulesListBlocks();
}
